import React, { FC, useEffect, useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import {
  RadioGroup,
  FormGroup,
  useTheme,
  Typography,
  Grid,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import { find, keyBy, mapValues } from 'lodash'
import { Controller, FormProvider, FormState, Path, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { clearReports } from '../../pages/Reports/ReportsSlice'
import { disableMassFill } from '../../pages/TradingLog'
import {
  getJournalingOptionTranslationKey,
  JournalingOptionCategory,
} from '../../pages/TradingLog/tradingLogLabels'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { updateLocalJournalingProgress } from '../../store/reducers/journalingReducerSlice'
import { focusKeyPressNext } from '../../utils'
import { useIsMobile } from '../../utils/useIsMobile'
import { CustomButton } from '../CustomButton'
import { showToast } from '../Toasts'

import { CheckboxButton } from './CheckboxButton'
import JournalingModalSectionTitle from './JournalingModalSectionTitle'
import { JournalingService } from './JournalingService'
import { RadioButton } from './RadioButton'
import {
  CustomValueModalTypeEnum,
  JournalingForm,
  JournalingModalProps,
  JournalOptions,
  Psychology,
  RiskAnswer,
} from './types'

const toFalseMap = (items?: { name: string }[]) => mapValues(keyBy(items, 'name'), () => false)
const toNullMap = (items?: { name: string }[]) => mapValues(keyBy(items, 'name'), () => null)
const extractAnswers = (items: { name: string; answer: string | boolean | null }[] = []) =>
  mapValues(keyBy(items, 'name'), 'answer')

export const JournalingModal: FC<JournalingModalProps> = ({ isOpen, onClose, tradingLogId }) => {
  const { spacing, palette } = useTheme()
  const isMobile = useIsMobile('md')

  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)
  const { localJournalingProgressData } = useAppSelector((state) => state.localJournalingProgress)
  const { massFillEnabled, selectedTrades } = useAppSelector((state) => state.tradingLog)

  const [journalOptions, setJournalOptions] = useState<JournalOptions>()
  const [customValueModalType, setCustomValueModalType] = useState<CustomValueModalTypeEnum | null>(
    null,
  )
  const [customValueName, setCustomValueName] = useState('')
  const [customValuesError, setCustomValuesError] = useState('')
  const isCustomValueModalOpen = customValueModalType !== null

  const [customStrategies, setCustomStrategies] = useState<{ name: string; answer: boolean }[]>([])
  const [customTradingConfluences, setCustomTradingConfluences] = useState<
    { name: string; answer: boolean }[]
  >([])

  const [isLoading, setIsLoading] = useState<boolean | null>(null)

  const getJournalingOptionLabel = (name: string, category: JournalingOptionCategory = 'default') =>
    t(
      `tradingLogPage.journaling.options.${getJournalingOptionTranslationKey(name, category)}`,
      name,
    )

  const methods = useForm<JournalingForm>({
    mode: 'onSubmit',
  })
  const { watch, setValue, reset, formState, control, handleSubmit } = methods
  const isPositive =
    watch('isPositive') === undefined ? journalOptions?.psychology.isPositive : watch('isPositive')

  const handleClose = () => {
    onClose()
    dispatch(disableMassFill())
  }

  const handleFeelingChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setValue('isPositive', ev.target.value === Psychology.positive, { shouldDirty: true })
  }

  const handleRisksChange = (
    ev: React.ChangeEvent<HTMLInputElement>,
    fieldName: Path<JournalingForm>,
  ) => {
    setValue(fieldName, ev.target.value === RiskAnswer.yes, { shouldDirty: true })
  }

  const handlePlanChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setValue('followedPlan', ev.target.value === RiskAnswer.yes, { shouldDirty: true })
  }

  const handlerEnter = (ev: React.KeyboardEvent, fState: FormState<JournalingForm>) => {
    return focusKeyPressNext(ev, fState)
  }

  const addCustomStrategy = () => {
    const formattedName = customValueName
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')

    const strategyExists = customStrategies.some((strategy) => strategy.name === formattedName)

    if (strategyExists) {
      setCustomValuesError(t('tradingLogPage.journaling.validation.alreadyExists') as string)
    } else {
      setCustomStrategies((prevState) => [...prevState, { name: formattedName, answer: false }])
      closeCustomValueModal()
    }
  }

  const deleteCustomStrategy = (strategy: string) => {
    const updatedStrategies = customStrategies.filter(({ name }) => name !== strategy)
    setValue(`customTradingStrategy.${strategy}`, false, { shouldDirty: true })
    setCustomStrategies(updatedStrategies)
  }

  const addCustomTradingConfluence = () => {
    const formattedName = customValueName
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')

    const alreadyExists = customTradingConfluences.some((item) => item.name === formattedName)

    if (alreadyExists) {
      setCustomValuesError(t('tradingLogPage.journaling.validation.alreadyExists') as string)
    } else {
      setCustomTradingConfluences((prevState) => [
        ...prevState,
        { name: formattedName, answer: false },
      ])
      closeCustomValueModal()
    }
  }

  const deleteCustomTradingConfluence = (name: string) => {
    const updatedTradingConfluences = customTradingConfluences.filter((item) => item.name !== name)
    setValue(`customTradingConfluence.${name}`, false, { shouldDirty: true })
    setCustomTradingConfluences(updatedTradingConfluences)
  }

  const addCustomValue = () => {
    switch (customValueModalType) {
      case CustomValueModalTypeEnum.TRADING_STRATEGY:
        addCustomStrategy()
        break
      case CustomValueModalTypeEnum.TRADING_CONFLUENCES:
        addCustomTradingConfluence()
        break
      default:
        break
    }
  }

  const openCustomValueModal = (type: CustomValueModalTypeEnum) => {
    setCustomValueModalType(type)
    setCustomValueName('')
    setCustomValuesError('')
  }

  const closeCustomValueModal = () => {
    setCustomValueModalType(null)
    setCustomValueName('')
    setCustomValuesError('')
  }

  const calcUniqueState = (tradingLogId: string) =>
    localJournalingProgressData.filter((item) => item.tradingLogId !== tradingLogId)

  const onSubmit = async (data: JournalingForm) => {
    try {
      const psychology = {
        isPositive: data.isPositive,
        positiveComments: [] as string[],
        negativeComments: [] as string[],
        emotionTags: [] as string[],
        followedPlan: data.followedPlan,
        brokenRules: [] as string[],
        journalNote: massFillEnabled ? null : data.journalNote?.trim() || null,
      }

      const tradingStrategy = {
        strategies: [] as string[],
        customStrategies: [] as string[],
      }

      const tradingConfluence = {
        tradingConfluences: [] as string[],
        customTradingConfluences: [] as string[],
      }

      const riskManagement = []
      if (journalOptions) {
        if (data.isPositive) {
          for (const comment of journalOptions.psychology.positiveComments) {
            if (data.positiveComments[comment.name]) {
              psychology.positiveComments.push(comment.name)
            }
          }
        } else if (data.isPositive === false) {
          for (const comment of journalOptions.psychology.negativeComments) {
            if (data.negativeComments[comment.name]) {
              psychology.negativeComments.push(comment.name)
            }
          }
        }

        for (const emotion of journalOptions.psychology.emotionTags) {
          if (data.emotionTags[emotion.name]) {
            psychology.emotionTags.push(emotion.name)
          }
        }

        if (data.followedPlan === false) {
          for (const rule of journalOptions.psychology.brokenRules) {
            if (data.brokenRules[rule.name]) {
              psychology.brokenRules.push(rule.name)
            }
          }
        }

        for (const strategy of journalOptions.tradingStrategy.strategies) {
          if (data.tradingStrategy[strategy.name]) {
            tradingStrategy.strategies.push(strategy.name)
          }
        }

        customStrategies.forEach((strategy) => {
          if (data.customTradingStrategy[strategy.name]) {
            tradingStrategy.customStrategies.push(strategy.name)
          }
        })

        for (const item of journalOptions.tradingConfluence?.tradingConfluences) {
          if (data.tradingConfluence[item.name]) {
            tradingConfluence.tradingConfluences.push(item.name)
          }
        }

        customTradingConfluences.forEach((item) => {
          if (data.customTradingConfluence[item.name]) {
            tradingConfluence.customTradingConfluences.push(item.name)
          }
        })

        for (const risk of journalOptions.riskManagement) {
          riskManagement.push({
            name: risk.name,
            answer: data.riskManagement[risk.name],
          })
        }

        if (tradingLogId) {
          const response = await JournalingService.sendJournalData({
            tradingLogId,
            psychology,
            tradingStrategy,
            tradingConfluence,
            riskManagement,
            accountId: currentAccount?.id,
          })

          if (response.success) {
            dispatch(
              updateLocalJournalingProgress([
                ...calcUniqueState(tradingLogId),
                { tradingLogId, journalingProgress: response.data },
              ]),
            )
            dispatch(clearReports())
          }
        }

        if (massFillEnabled) {
          const selectedTradesId = selectedTrades.map((trade) => trade.id)
          const response = await JournalingService.sendMassFillJournalData({
            selectedTrades: selectedTradesId,
            psychology,
            tradingStrategy,
            tradingConfluence,
            riskManagement,
            accountId: currentAccount?.id,
          })

          if (response.success) {
            const newLocalState = selectedTrades.map((trade) => ({
              tradingLogId: trade.id.toString(),
              journalingProgress: response.data,
            }))

            dispatch(
              updateLocalJournalingProgress([
                ...localJournalingProgressData.filter(
                  (item) => !selectedTradesId.includes(Number(item.tradingLogId)),
                ),
                ...newLocalState,
              ]),
            )

            dispatch(clearReports())
          }
        }

        showToast.success(t('tradingLogPage.journaling.toasts.updated'))
      }
    } catch (error) {
      showToast.error(error.message)
    } finally {
      handleClose()
    }
  }

  const handleResetForm = () => {
    const positiveComments = toFalseMap(journalOptions?.psychology?.positiveComments)
    const negativeComments = toFalseMap(journalOptions?.psychology?.negativeComments)
    const emotionTags = toFalseMap(journalOptions?.psychology?.emotionTags)
    const brokenRules = toFalseMap(journalOptions?.psychology?.brokenRules)
    const tradingStrategy = toFalseMap(journalOptions?.tradingStrategy?.strategies)
    const customTradingStrategy = toFalseMap(journalOptions?.tradingStrategy?.customStrategies)
    const tradingConfluence = toFalseMap(journalOptions?.tradingConfluence?.tradingConfluences)
    const customTradingConfluence = toFalseMap(
      journalOptions?.tradingConfluence?.customTradingConfluences,
    )
    const riskManagement = toNullMap(journalOptions?.riskManagement)

    const resetPayload = {
      isPositive: null,
      emotionTags,
      followedPlan: null,
      brokenRules,
      journalNote: '',
      positiveComments,
      negativeComments,
      tradingStrategy,
      customTradingStrategy,
      tradingConfluence,
      customTradingConfluence,
      riskManagement,
    }

    reset(resetPayload)
  }

  const retrieveJournalingFormValues = (options: JournalOptions): JournalingForm => ({
    isPositive: options.psychology.isPositive,
    emotionTags: extractAnswers(options.psychology.emotionTags),
    followedPlan: options.psychology.followedPlan,
    brokenRules: extractAnswers(options.psychology.brokenRules),
    journalNote: options.psychology.journalNote || '',
    positiveComments: extractAnswers(options.psychology.positiveComments),
    negativeComments: extractAnswers(options.psychology.negativeComments),
    tradingStrategy: extractAnswers(options.tradingStrategy.strategies),
    customTradingStrategy: extractAnswers(options.tradingStrategy.customStrategies),
    tradingConfluence: extractAnswers(options.tradingConfluence.tradingConfluences),
    customTradingConfluence: extractAnswers(options.tradingConfluence.customTradingConfluences),
    riskManagement: extractAnswers(options.riskManagement),
  })

  useEffect(() => {
    const getJournalData = async () => {
      try {
        setIsLoading(true)

        const { data } = await JournalingService.getJournalData(tradingLogId)
        setJournalOptions(data)
        setCustomStrategies(data.tradingStrategy.customStrategies)
        setCustomTradingConfluences(data.tradingConfluence.customTradingConfluences)

        const initialFormValues = retrieveJournalingFormValues(data)

        reset(initialFormValues)
      } catch (error) {
        showToast.error(error.message)
      } finally {
        setIsLoading(false)
      }
    }

    getJournalData()
  }, [tradingLogId])

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth={'lg'}
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          component: 'form',
          id: 'forNextFocus',
          onSubmit: handleSubmit(onSubmit),
          onKeyDown: (ev: React.KeyboardEvent<HTMLFormElement>) => {
            handlerEnter(ev, methods.formState)
          },
        }}
      >
        <Stack
          direction={'row'}
          pr={2}
          flexWrap="nowrap"
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          <DialogTitle
            variant="h2"
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              flex: 1,
            }}
          >
            {t('tradingLogPage.journaling.title', 'Analytical questionnaire')}
          </DialogTitle>

          <IconButton className="close-button" aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* {isLoading ? (
          <DialogContent
            dividers
            sx={{
              minHeight: '300px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              // minHeight: 0, // important to prevent flexbox from overflowing
            }}
          >
            <CircularProgress />
          </DialogContent>
        ) : ( */}
        <DialogContent dividers>
          <FormProvider {...methods}>
            <Box p={isMobile ? 0 : 3}>
              <JournalingModalSectionTitle
                step={1}
                title={t('tradingLogPage.journaling.sections.feeling')}
              />
              <FormGroup
                sx={{
                  p: isMobile ? spacing(3, 0, 7, 4.25) : spacing(3, 0, 7, 4.25),
                  borderLeft: `1px solid ${palette.divider}`,
                }}
              >
                {journalOptions && (
                  <Controller
                    control={control}
                    name={'isPositive'}
                    defaultValue={journalOptions.psychology.isPositive}
                    render={({ field }) => (
                      <RadioGroup
                        {...field}
                        onChange={handleFeelingChange}
                        sx={{ flexDirection: 'row', gap: 2 }}
                      >
                        <RadioButton label={'positive'} checked={isPositive === true} />
                        <RadioButton label={'negative'} checked={isPositive === false} />
                      </RadioGroup>
                    )}
                  />
                )}
                {isPositive ? (
                  <Grid container mt={6} gap={3}>
                    <Grid item>
                      <Typography variant="h6">
                        {t('tradingLogPage.journaling.labels.comment', 'Comment')}
                      </Typography>
                    </Grid>
                    <Grid item display="flex" flexWrap="wrap" gap={2}>
                      {journalOptions &&
                        journalOptions.psychology.positiveComments.map(({ name, answer }) => (
                          <CheckboxButton
                            key={name}
                            name={`positiveComments.${name}`}
                            label={getJournalingOptionLabel(name)}
                            defaultValue={answer}
                          />
                        ))}
                    </Grid>
                  </Grid>
                ) : isPositive === false ? (
                  <Grid container mt={6} gap={3}>
                    <Grid item>
                      <Typography variant="h6">
                        {t('tradingLogPage.journaling.labels.comment', 'Comment')}
                      </Typography>
                    </Grid>
                    <Grid item display="flex" flexWrap="wrap" gap={2}>
                      {journalOptions &&
                        journalOptions.psychology.negativeComments.map(({ name, answer }) => (
                          <CheckboxButton
                            key={name}
                            name={`negativeComments.${name}`}
                            label={getJournalingOptionLabel(name)}
                            defaultValue={answer}
                          />
                        ))}
                    </Grid>
                  </Grid>
                ) : null}
              </FormGroup>

              <JournalingModalSectionTitle
                step={2}
                title={t('tradingLogPage.journaling.sections.emotions', 'Emotional state')}
              />
              <FormGroup
                sx={{
                  flexDirection: 'row',
                  gap: 2,
                  p: spacing(4, 0, 7, 4.25),
                  borderLeft: `1px solid ${palette.grey[500]}`,
                }}
              >
                {journalOptions &&
                  journalOptions.psychology.emotionTags.map(({ name, answer }) => (
                    <CheckboxButton
                      key={name}
                      name={`emotionTags.${name}`}
                      label={getJournalingOptionLabel(name)}
                      defaultValue={answer}
                    />
                  ))}
              </FormGroup>

              <JournalingModalSectionTitle
                step={3}
                title={t('tradingLogPage.journaling.sections.planAdherence', 'Plan adherence')}
              />
              <FormGroup
                sx={{
                  p: spacing(3, 0, 7, 4.25),
                  borderLeft: `1px solid ${palette.divider}`,
                }}
              >
                <Typography variant="h6" mb={2}>
                  {t('tradingLogPage.journaling.labels.followedPlan', 'Did you follow your plan?')}
                </Typography>
                <Controller
                  control={control}
                  name={'followedPlan'}
                  defaultValue={journalOptions?.psychology.followedPlan ?? null}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      onChange={handlePlanChange}
                      sx={{ flexDirection: 'row', gap: 2 }}
                    >
                      <RadioButton label={'yes'} checked={watch('followedPlan') === true} />
                      <RadioButton label={'no'} checked={watch('followedPlan') === false} />
                    </RadioGroup>
                  )}
                />

                {watch('followedPlan') === false && (
                  <Grid container mt={6} gap={3}>
                    <Grid item>
                      <Typography variant="h6">
                        {t(
                          'tradingLogPage.journaling.labels.brokenRules',
                          'Which rule was broken?',
                        )}
                      </Typography>
                    </Grid>
                    <Grid item display="flex" flexWrap="wrap" gap={2}>
                      {journalOptions &&
                        journalOptions.psychology.brokenRules.map(({ name, answer }) => (
                          <CheckboxButton
                            key={name}
                            name={`brokenRules.${name}`}
                            label={getJournalingOptionLabel(name)}
                            defaultValue={answer}
                          />
                        ))}
                    </Grid>
                  </Grid>
                )}
              </FormGroup>

              <JournalingModalSectionTitle
                step={4}
                title={t('tradingLogPage.journaling.sections.strategy')}
              />
              <FormGroup
                sx={{
                  flexDirection: 'row',
                  gap: 2,
                  p: spacing(4, 0, 7, 4.25),
                  borderLeft: `1px solid ${palette.grey[500]}`,
                }}
              >
                {journalOptions &&
                  journalOptions.tradingStrategy.strategies.map(({ name, answer }) => (
                    <CheckboxButton
                      key={name}
                      name={`tradingStrategy.${name}`}
                      label={getJournalingOptionLabel(name, 'tradingStrategy')}
                      defaultValue={answer}
                    />
                  ))}
                {customStrategies.map(({ name, answer }) => (
                  <CheckboxButton
                    key={name}
                    name={`customTradingStrategy.${name}`}
                    label={getJournalingOptionLabel(name, 'tradingStrategy')}
                    defaultValue={answer}
                    customValue
                    showDeleteButton={
                      !find(journalOptions?.tradingStrategy.customStrategies, {
                        name,
                      })
                    }
                    deleteCustomValue={() => deleteCustomStrategy(name)}
                  />
                ))}
                <Button
                  color="inherit"
                  sx={{
                    width: 'fit-content',
                    height: spacing(5.5),
                    m: 0,
                    paddingX: 3,
                    border: `1px solid ${palette.grey[500]}`,
                    borderRadius: spacing(2.75),
                    fontWeight: 500,
                    lineHeight: spacing(2.75),
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'transparent',
                      borderColor: palette.grey[700],
                    },
                  }}
                  onClick={() => openCustomValueModal(CustomValueModalTypeEnum.TRADING_STRATEGY)}
                >
                  {t('tradingLogPage.journaling.actions.other', 'Other...')}
                </Button>
              </FormGroup>

              <JournalingModalSectionTitle
                step={5}
                title={t('tradingLogPage.journaling.sections.confluences')}
              />
              <FormGroup
                sx={{
                  flexDirection: 'row',
                  gap: 2,
                  p: spacing(4, 0, 7, 4.25),
                  borderLeft: `1px solid ${palette.grey[500]}`,
                }}
              >
                {journalOptions &&
                  journalOptions.tradingConfluence?.tradingConfluences.map(({ name, answer }) => (
                    <CheckboxButton
                      key={name}
                      name={`tradingConfluence.${name}`}
                      label={getJournalingOptionLabel(name, 'tradingConfluence')}
                      defaultValue={answer}
                    />
                  ))}
                {customTradingConfluences.map(({ name, answer }) => (
                  <CheckboxButton
                    key={name}
                    name={`customTradingConfluence.${name}`}
                    label={getJournalingOptionLabel(name, 'tradingConfluence')}
                    defaultValue={answer}
                    customValue
                    showDeleteButton={
                      !find(journalOptions?.tradingConfluence.customTradingConfluences, {
                        name,
                      })
                    }
                    deleteCustomValue={() => deleteCustomTradingConfluence(name)}
                  />
                ))}
                <Button
                  color="inherit"
                  sx={{
                    width: 'fit-content',
                    height: spacing(5.5),
                    m: 0,
                    paddingX: 3,
                    border: `1px solid ${palette.grey[500]}`,
                    borderRadius: spacing(2.75),
                    fontWeight: 500,
                    lineHeight: spacing(2.75),
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'transparent',
                      borderColor: palette.grey[700],
                    },
                  }}
                  onClick={() => openCustomValueModal(CustomValueModalTypeEnum.TRADING_CONFLUENCES)}
                >
                  {t('tradingLogPage.journaling.actions.other', 'Other...')}
                </Button>
              </FormGroup>

              <JournalingModalSectionTitle
                step={6}
                title={t('tradingLogPage.journaling.sections.riskManagement')}
              />
              <FormGroup
                sx={{
                  gap: 2,
                  p: spacing(4, 0, 7, 4.25),
                  borderLeft: `1px solid ${palette.grey[500]}`,
                }}
              >
                {journalOptions &&
                  journalOptions.riskManagement.map(({ name, answer }) => (
                    <Stack
                      key={name}
                      spacing={2}
                      direction={isMobile ? 'column' : 'row'}
                      alignItems={isMobile ? 'flex-start' : 'center'}
                      justifyContent={isMobile ? 'center' : 'space-between'}
                    >
                      <Typography variant="h6">{getJournalingOptionLabel(name)}</Typography>

                      <Controller
                        control={control}
                        name={`riskManagement.${name}`}
                        defaultValue={answer}
                        render={({ field }) => (
                          <RadioGroup
                            {...field}
                            onChange={(ev) => handleRisksChange(ev, `riskManagement.${name}`)}
                            sx={{ flexDirection: 'row', gap: 2 }}
                          >
                            <RadioButton
                              label={'yes'}
                              checked={
                                watch(`riskManagement.${name}`) === undefined
                                  ? answer === true
                                  : watch(`riskManagement.${name}`) === true
                              }
                              width={12}
                            />
                            <RadioButton
                              label={'no'}
                              checked={
                                watch(`riskManagement.${name}`) === undefined
                                  ? answer === false
                                  : watch(`riskManagement.${name}`) === false
                              }
                              width={12}
                            />
                          </RadioGroup>
                        )}
                      />
                    </Stack>
                  ))}
              </FormGroup>

              {!massFillEnabled && (
                <>
                  <JournalingModalSectionTitle
                    step={7}
                    title={t('tradingLogPage.journaling.sections.journalNote', 'Journal note')}
                  />
                  <FormGroup
                    sx={{
                      gap: 2,
                      pt: spacing(4),
                      pr: 0,
                      pb: 0,
                      pl: spacing(4.25),
                      // borderLeft: `1px solid ${palette.grey[500]}`,
                    }}
                  >
                    <Controller
                      control={control}
                      name="journalNote"
                      defaultValue={journalOptions?.psychology.journalNote || ''}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          minRows={5}
                          multiline
                          fullWidth
                          label={t(
                            'tradingLogPage.journaling.labels.journalNote',
                            'What happened, what did you think, and how did you execute?',
                          )}
                        />
                      )}
                    />

                    {journalOptions?.psychology.sentimentAnalysis?.enabled && (
                      <Stack
                        spacing={1}
                        p={2}
                        border={`1px solid ${palette.divider}`}
                        borderRadius={2}
                      >
                        <Typography variant="subtitle2">
                          {t('tradingLogPage.journaling.labels.aiSentiment', 'AI journal analysis')}
                        </Typography>
                        <Typography variant="body2">
                          {t('sentiment', 'Sentiment')}:{' '}
                          {journalOptions.psychology.sentimentAnalysis.sentimentLabel || '-'}
                        </Typography>
                        <Typography variant="body2">
                          {t('confidence', 'Confidence')}:{' '}
                          {journalOptions.psychology.sentimentAnalysis.confidence ?? '-'}
                        </Typography>
                        <Typography variant="body2">
                          {t('discipline', 'Discipline')}:{' '}
                          {journalOptions.psychology.sentimentAnalysis.disciplineScore ?? '-'}
                        </Typography>
                        {!!journalOptions.psychology.sentimentAnalysis.suggestedEmotionTags
                          .length && (
                          <Typography variant="body2">
                            {t(
                              'tradingLogPage.journaling.labels.aiSuggestedEmotionTags',
                              'Suggested emotion tags',
                            )}
                            :{' '}
                            {journalOptions.psychology.sentimentAnalysis.suggestedEmotionTags.join(
                              ', ',
                            )}
                          </Typography>
                        )}
                        {!!journalOptions.psychology.sentimentAnalysis.suggestedRuleBreaks
                          .length && (
                          <Typography variant="body2">
                            {t(
                              'tradingLogPage.journaling.labels.aiSuggestedRuleBreaks',
                              'Suggested rule breaks',
                            )}
                            :{' '}
                            {journalOptions.psychology.sentimentAnalysis.suggestedRuleBreaks.join(
                              ', ',
                            )}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </FormGroup>
                </>
              )}
            </Box>
          </FormProvider>
        </DialogContent>
        {/* )} */}

        <DialogActions>
          {formState.isDirty && (
            <CustomButton
              onClick={handleResetForm}
              color="error"
              disabled={formState.isSubmitting || !!isLoading}
            >
              {t('tradingLogPage.journaling.actions.resetForm', 'Reset form')}
            </CustomButton>
          )}

          <CustomButton
            id="save_journaling"
            type="submit"
            width={spacing(17)}
            disabled={
              (!formState.isDirty && !massFillEnabled) || formState.isSubmitting || !!isLoading
            }
          >
            {t('tradingLogPage.journaling.actions.save', 'Save')}
          </CustomButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isCustomValueModalOpen}
        PaperProps={{
          sx: {
            gap: 5,
            padding: 5,
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Typography fontSize={32} fontWeight="bold">
            {customValueModalType === CustomValueModalTypeEnum.TRADING_STRATEGY
              ? t('tradingLogPage.journaling.customValue.addTradingStrategy')
              : t('tradingLogPage.journaling.customValue.addTradingConfluence')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <TextField
            fullWidth
            label={
              customValueModalType === CustomValueModalTypeEnum.TRADING_STRATEGY
                ? t('tradingLogPage.journaling.customValue.tradingStrategy')
                : t('tradingLogPage.journaling.customValue.tradingConfluence')
            }
            variant="standard"
            value={customValueName}
            error={!!customValuesError}
            helperText={customValuesError}
            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
              setCustomValueName(ev.target.value)
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 0 }}>
          <CustomButton color="secondary" width={spacing(17)} onClick={closeCustomValueModal}>
            {t('tradingLogPage.journaling.actions.cancel', 'Cancel')}
          </CustomButton>
          <CustomButton
            width={spacing(17)}
            onClick={addCustomValue}
            variant="contained"
            color="primary"
            disabled={!customValueName}
          >
            {t('tradingLogPage.journaling.actions.confirm', 'Confirm')}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
